import { requestedWorkspace, workspaceDestination } from "./utils/workspaceDestination";
import { useLocation } from "react-router";
import { creditFetch } from "./utils/creditPilot";
// Canonical consolidated user dashboard
/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import "./UserDashboard.css";
import AutoPromoteLogo from './components/AutoPromoteLogo';
import ProfilePanel from "./UserDashboardTabs/ProfilePanel";
import UploadPanel from "./UserDashboardTabs/UploadPanel";
import ClipStudioWorkspace from "./UserDashboardTabs/ClipStudioWorkspace";
import ViralClipStudioPanel from "./UserDashboardTabs/ViralClipStudioPanel";
import SchedulesPanel from "./UserDashboardTabs/SchedulesPanel";
import RewardsPanel from "./UserDashboardTabs/RewardsPanel";
import NotificationsPanel from "./UserDashboardTabs/NotificationsPanel";

import ConnectionsPanel from "./UserDashboardTabs/ConnectionsPanelRedesign";
import PayPalSubscriptionPanel from "./components/PayPalSubscriptionPanel";
import AdminAuditViewer from "./AdminAuditViewer";
import SecurityPanel from "./UserDashboardTabs/SecurityPanel";
// CommunityPanel and CommunityFeed removed
import WolfHuntDashboard from "./EngagementMarketplace";
import MissionControlPanel from "./UserDashboardTabs/MissionControlPanel";
import CamCombinerPanel from "./UserDashboardTabs/CamCombinerPanel";
import TeamPanel from "./UserDashboardTabs/TeamPanel";
// LiveWatch import removed
// LiveHub import removed
import FloatingActions from "./components/FloatingActions";
import TopNav from "./components/TopNav";
import BottomNav from "./components/BottomNav";
import VoiceOverGuide from "./components/VoiceOverGuide";
import { ProductAssistanceLauncher } from "./components/ProductAssistance";
import AdminKyc from "./AdminKyc";
import { auth } from "./firebaseClient";
import { sendEmailVerification } from "firebase/auth";
import { API_ENDPOINTS, API_BASE_URL, ENABLE_WOLF_HUNT } from "./config";
import toast, { Toaster } from "react-hot-toast";
import { cachedFetch, batchWithDelay, clearCache } from "./utils/requestCache";
import { startVisiblePoller } from "./utils/visiblePoller";
import { isAllowedAuthUrl } from "./utils/isAllowedAuthUrl";
import {
  getActiveWorkspaceId,
  getActiveWorkspaceRole,
  withWorkspaceHeaders,
} from "./utils/workspace";
import usePlatformStatus from "./hooks/usePlatformStatus";
import useTelegramConnection from "./hooks/useTelegramConnection";
import TelegramConnectDialog from "./components/TelegramConnectDialog";
import "./DashboardRedesign.css";

const DEFAULT_IMAGE = `${process.env.PUBLIC_URL || ""}/image.png`;

const DASHBOARD_PAGE_META = {
  profile: {
    eyebrow: "Workspace",
    title: "Overview",
    description: "See the health of your content operation and jump into your next task.",
  },
  connections: {
    eyebrow: "Workspace",
    title: "Connections",
    description: "Connect and manage every channel AutoPromote can publish to.",
  },
  team: {
    eyebrow: "Workspace",
    title: "Team",
    description: "Manage collaborators, workspace access, and available seats.",
  },
  upload: {
    eyebrow: "Create",
    title: "Publisher",
    description: "Prepare one release. Complete every destination.",
  },
  cam_combiner: {
    eyebrow: "Create",
    title: "Cam Combiner",
    description: "Sync multiple podcast cameras and master audio into one ready-to-edit timeline.",
  },
  clip_studio: {
    eyebrow: "Create",
    title: "Find Viral Clips",
    description:
      "Analyse a finished video and review the moments most worth turning into short clips.",
  },
  viral_clip_studio: {
    eyebrow: "Create",
    title: "Viral Clip Studio",
    description:
      "Edit detected moments on the full timeline, refine hooks, and render short clips.",
  },
  schedules: {
    eyebrow: "Publish",
    title: "Schedule",
    description: "Create once. Publish everywhere.",
  },
  billing: {
    eyebrow: "Settings",
    title: "Billing",
    description: "Manage your plan, publishing capacity, and workspace credits.",
  },
  security: {
    eyebrow: "Settings",
    title: "Security",
    description: "Protect your account, sessions, privacy preferences, and connected platforms.",
  },
  notifications: {
    eyebrow: "Workspace",
    title: "Notifications",
    description: "Review account updates, publishing events, and actions that need your attention.",
  },
  "admin-audit": {
    eyebrow: "Administration",
    title: "Admin Audit",
    description: "Review administrative activity across the platform.",
  },
  "admin-kyc": {
    eyebrow: "Administration",
    title: "Admin KYC",
    description: "Review identity verification submissions and account status.",
  },
  wolf_hunt: {
    eyebrow: "Labs",
    title: "Mission Board",
    description: "Review available missions and community campaign activity.",
  },
};

const DASHBOARD_NAV_GROUPS = [
  {
    label: "",
    items: [{ id: "profile", label: "Overview", icon: "overview" }],
  },
  {
    label: "Create",
    items: [
      { id: "cam_combiner", label: "Cam Combiner", icon: "camera", desktopOnly: true },
      { id: "clip_studio", label: "Find Viral Clips", icon: "clips" },
      { id: "viral_clip_studio", label: "Viral Clip Studio", icon: "clips" },
    ],
  },
  {
    label: "Publish",
    items: [
      { id: "upload", label: "Publisher", icon: "publish" },
      { id: "schedules", label: "Queue", icon: "queue" },
    ],
  },
  {
    label: "Settings",
    items: [
      { id: "connections", label: "Connections", icon: "connections" },
      { id: "team", label: "Team", icon: "team" },
      { id: "billing", label: "Billing", icon: "billing" },
      { id: "security", label: "Security", icon: "security" },
    ],
  },
];

function DashboardIcon({ name, size = 18 }) {
  const sharedProps = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (name) {
    case "overview":
      return (
        <svg {...sharedProps}>
          <rect x="3" y="3" width="7" height="7" rx="2" />
          <rect x="14" y="3" width="7" height="7" rx="2" />
          <rect x="3" y="14" width="7" height="7" rx="2" />
          <rect x="14" y="14" width="7" height="7" rx="2" />
        </svg>
      );
    case "connections":
      return (
        <svg {...sharedProps}>
          <path d="M10 13a5 5 0 0 0 7.1.1l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1" />
          <path d="M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1" />
        </svg>
      );
    case "team":
      return (
        <svg {...sharedProps}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case "publish":
      return (
        <svg {...sharedProps}>
          <path d="M12 16V4M7 9l5-5 5 5" />
          <path d="M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />
        </svg>
      );
    case "camera":
      return (
        <svg {...sharedProps}>
          <rect x="3" y="6" width="13" height="12" rx="3" />
          <path d="m16 10 5-3v10l-5-3z" />
        </svg>
      );
    case "sparkles":
      return (
        <svg {...sharedProps}>
          <path d="m12 3-1.3 3.2L7.5 7.5l3.2 1.3L12 12l1.3-3.2 3.2-1.3-3.2-1.3z" />
          <path d="m18.5 13-1 2.5-2.5 1 2.5 1 1 2.5 1-2.5 2.5-1-2.5-1z" />
          <path d="m5 13-.7 1.7-1.8.8 1.8.7L5 18l.7-1.8 1.8-.7-1.8-.8z" />
        </svg>
      );
    case "queue":
      return (
        <svg {...sharedProps}>
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M16 3v4M8 3v4M3 10h18M8 14h3M8 17h6" />
        </svg>
      );
    case "billing":
      return (
        <svg {...sharedProps}>
          <rect x="2.5" y="5" width="19" height="14" rx="3" />
          <path d="M2.5 10h19M7 15h3" />
        </svg>
      );
    case "security":
      return (
        <svg {...sharedProps}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case "audit":
      return (
        <svg {...sharedProps}>
          <path d="M9 5h10M9 12h10M9 19h10" />
          <circle cx="4" cy="5" r="1" />
          <circle cx="4" cy="12" r="1" />
          <circle cx="4" cy="19" r="1" />
        </svg>
      );
    case "identity":
      return (
        <svg {...sharedProps}>
          <rect x="3" y="4" width="18" height="16" rx="3" />
          <circle cx="9" cy="10" r="2.5" />
          <path d="M5.5 17c.7-2 2-3 3.5-3s2.8 1 3.5 3M15 9h3M15 13h3" />
        </svg>
      );
    case "target":
      return (
        <svg {...sharedProps}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="12" cy="12" r="1" />
        </svg>
      );
    case "clips":
      return (
        <svg {...sharedProps}>
          <circle cx="6" cy="7" r="3" />
          <circle cx="6" cy="17" r="3" />
          <path d="m8.5 8.5 11 7.5M8.5 15.5l11-7.5" />
        </svg>
      );
    case "notifications":
      return (
        <svg {...sharedProps}>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M14 21a2.5 2.5 0 0 1-4 0" />
        </svg>
      );
    case "logout":
      return (
        <svg {...sharedProps}>
          <path d="M10 17l5-5-5-5M15 12H3" />
          <path d="M14 3h5a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5" />
        </svg>
      );
    default:
      return (
        <svg {...sharedProps}>
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
  }
}

function DashboardNavItem({ item, activeTab, onNavigate }) {
  const isActive = activeTab === item.id;
  return (
    <li className={isActive ? "active" : ""}>
      <button
        type="button"
        onClick={() => onNavigate(item.id)}
        aria-current={isActive ? "page" : undefined}
      >
        <span className="dashboard-nav-icon">
          <DashboardIcon name={item.icon} />
        </span>
        <span>{item.label}</span>
      </button>
    </li>
  );
}

const UserDashboard = ({
  user,
  content,
  stats,
  badges = [],
  notifications = [],
  userDefaults,
  onSaveDefaults,
  onLogout,
  onUpload,
  mySchedules = [],
  mySchedulesLoading = false,
  mySchedulesError = "",
  onSchedulesChanged,
  scheduleScope,
  schedulesLoading,
  hasMoreSchedules,
  onScheduleScopeChanged,
  onLoadMoreSchedules,
  onWorkspaceChanged,
  onOpenAdminDashboard,
}) => {
  const hasPendingWorkspaceInvite =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("invite");
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(hasPendingWorkspaceInvite ? "team" : requestedWorkspace(location.search) || "profile");
  useEffect(() => {
    const requested = requestedWorkspace(location.search);
    if (requested && !hasPendingWorkspaceInvite) {
      setActiveTab(requested);
      hasAutoRoutedPrimaryTab.current = true;
    }
  }, [location.search, hasPendingWorkspaceInvite]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window === "undefined" ? false : window.matchMedia("(max-width: 1023px)").matches
  );
  const testerAccess = user?.testerAccess || user?.user?.testerAccess || null;
  const testerAccessActive =
    testerAccess?.status === "active" && Date.parse(testerAccess?.expiresAt || "") > Date.now();

  // If Wolf Hunt is disabled, prevent accidentally staying on that tab
  useEffect(() => {
    if (!ENABLE_WOLF_HUNT && activeTab === "wolf_hunt") {
      setActiveTab("profile");
    }
  }, [activeTab]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const handleViewportChange = event => setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", handleViewportChange);
    return () => mediaQuery.removeEventListener?.("change", handleViewportChange);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle("dashboard-menu-open", sidebarOpen);
    return () => document.body.classList.remove("dashboard-menu-open");
  }, [sidebarOpen]);

  useEffect(() => {
    if (isMobileViewport && activeTab === "cam_combiner") {
      setActiveTab("profile");
      setSidebarOpen(false);
      toast("Cam Combiner needs a desktop-sized screen. Your other tools are ready on mobile.", {
        icon: "🖥️",
      });
    }
  }, [activeTab, isMobileViewport]);

  const [notifs, setNotifs] = useState(
    Array.isArray(notifications) ? notifications.filter(notification => !notification?.read) : []
  );
  const notifiedNotificationIdsRef = useRef(
    new Set(
      (Array.isArray(notifications) ? notifications : [])
        .map(notification => notification?.id)
        .filter(Boolean)
    )
  );
  const notificationSessionStartedAtRef = useRef(Date.now());
  const didHydrateNotificationPollRef = useRef(false);
  const [uploadLaunchTab, setUploadLaunchTab] = useState(null);
  const [billingReturnTab, setBillingReturnTab] = useState("profile");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [platformOptions, setPlatformOptions] = useState({});
  const [spotifySelectedTracks, setSpotifySelectedTracks] = useState([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [rotate, setRotate] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [template, setTemplate] = useState("none");
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [duration, setDuration] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("video");
  const [scheduleMode, setScheduleMode] = useState("auto");
  const [manualWhen, setManualWhen] = useState("");
  const [frequency, setFrequency] = useState("once");
  const [tz, setTz] = useState(userDefaults?.timezone || "UTC");
  const [defaultsPlatforms, setDefaultsPlatforms] = useState(
    Array.isArray(userDefaults?.defaultPlatforms) ? userDefaults.defaultPlatforms : []
  );
  const [defaultsFrequency, setDefaultsFrequency] = useState(
    userDefaults?.defaultFrequency || "once"
  );
  const [paypalEmail, setPaypalEmail] = useState(
    userDefaults?.paypalEmail || user?.paypalEmail || ""
  );

  const [scheduleContentMap, setScheduleContentMap] = useState({});

  const BILLING_RETURN_TAB_LABELS = {
    profile: "Overview",
    connections: "Connections",
    team: "Team",
    upload: "Uploads",
    schedules: "Queue",
    cam_combiner: "Cam Combiner",
    clip_studio: "Find Viral Clips",
    viral_clip_studio: "Viral Clip Studio",
    security: "Security",
    notifications: "Notifications",
    "admin-audit": "Admin Audit",
    "admin-kyc": "Admin KYC",
    wolf_hunt: "Mission Board",
    ads: "Rewards",
    rewards: "Rewards",
    live: "Live",
  };

  // Platform statuses managed by the usePlatformStatus hook
  const {
    statuses: platformStatuses,
    platformMetadata,
    platformSummary,
    setStatus: setPlatformStatusByName,
    loadAllUnified: loadAllPlatformStatusesUnified,
    loadPlatformStatus,
  } = usePlatformStatus();

  // Aliases so existing prop names and JSX references stay unchanged
  const discordStatus = platformStatuses.discord;
  const linkedinStatus = platformStatuses.linkedin;
  const telegramStatus = platformStatuses.telegram;
  const pinterestStatus = platformStatuses.pinterest;
  const redditStatus = platformStatuses.reddit;
  const spotifyStatus = platformStatuses.spotify;
  const youtubeStatus = platformStatuses.youtube;
  const twitterStatus = platformStatuses.twitter;
  const snapchatStatus = platformStatuses.snapchat;
  const tiktokStatus = platformStatuses.tiktok;
  const facebookStatus = platformStatuses.facebook;

  const [connectBanner, setConnectBanner] = useState(null);
  const [systemHealth, setSystemHealth] = useState({ ok: true, status: "unknown", message: null });

  const [afterdarkRefreshKey, setAfterdarkRefreshKey] = useState(0);
  const [pinterestCreateVisible, setPinterestCreateVisible] = useState(false);
  const [pinterestCreateName, setPinterestCreateName] = useState("");
  const [pinterestCreateDesc, setPinterestCreateDesc] = useState("");
  const hasLoadedPlatformStatus = useRef(false);
  const selectedVideoRef = useRef(null);
  const hasAutoRoutedPrimaryTab = useRef(false);
  const contentList = useMemo(() => (Array.isArray(content) ? content : []), [content]);
  const schedulesList = useMemo(
    () => (Array.isArray(mySchedules) ? mySchedules : []),
    [mySchedules]
  );
  const hasAfterDarkAccess = !!(
    user &&
    (user.isAdmin ||
      user.role === "admin" ||
      user.kycVerified ||
      (user.flags && user.flags.afterDarkAccess))
  );
  const isAdminUser = !!(user && (user.isAdmin || user.role === "admin"));
  const needsKyc = !!(user && !user.kycVerified);
  const connectedPlatformCount = useMemo(() => {
    const rawPlatforms =
      platformSummary && platformSummary.raw ? Object.values(platformSummary.raw) : [];
    return rawPlatforms.filter(platform => platform && platform.connected).length;
  }, [platformSummary]);
  const hasConnectedPlatforms = connectedPlatformCount > 0;

  const [emailVerified, setEmailVerified] = useState(true);
  useEffect(() => {
    setTz(userDefaults?.timezone || "UTC");
    setDefaultsPlatforms(
      Array.isArray(userDefaults?.defaultPlatforms) ? userDefaults.defaultPlatforms : []
    );
    setDefaultsFrequency(userDefaults?.defaultFrequency || "once");
    setPaypalEmail(userDefaults?.paypalEmail || user?.paypalEmail || "");
  }, [userDefaults, user?.paypalEmail]);

  useEffect(() => {
    const unreadNotifications = Array.isArray(notifications)
      ? notifications.filter(notification => !notification?.read)
      : [];
    unreadNotifications.forEach(notification => {
      if (notification?.id) notifiedNotificationIdsRef.current.add(notification.id);
    });

    setNotifs(prevNotifs => {
      const previous = Array.isArray(prevNotifs) ? prevNotifs : [];
      if (previous.length !== unreadNotifications.length) {
        return unreadNotifications;
      }

      const hasChanged = previous.some((notification, index) => {
        const nextNotification = unreadNotifications[index];
        return (
          notification?.id !== nextNotification?.id ||
          notification?.read !== nextNotification?.read ||
          notification?.message !== nextNotification?.message ||
          notification?.title !== nextNotification?.title ||
          notification?.created_at !== nextNotification?.created_at
        );
      });

      return hasChanged ? unreadNotifications : previous;
    });
  }, [notifications]);

  useEffect(() => {
    // NEW: Check for "Wolf Hunt" onboarding criteria
    // Triggers if user has >= 2 content items AND hasn't seen the welcome yet
    if (!ENABLE_WOLF_HUNT) return; // Do not show recruitment notice when Wolf Hunt is locked

    if (contentList && contentList.length >= 2) {
      const hasSeen = localStorage.getItem("wolfHuntWelcomeSeen");

      if (!hasSeen) {
        // Trigger the special notification logic
        // Mark as seen so it doesn't fire again immediately on refresh
        localStorage.setItem("wolfHuntWelcomeSeen", "true");

        // 1. Add to Notifications Panel
        const welcomeMsg = {
          id: "wolf-hunt-welcome-" + Date.now(),
          title: "🐺 WOLF HUNT INVITATION",
          message:
            "Soldier! You have proven your worth by publishing content. The Wolf Hunt awaits. Go to the Wolf Hunt tab and click the Speaker icon for your mission briefing.",
          type: "wolf_hunt_invite",
          read: false,
          created_at: new Date().toISOString(),
        };

        setNotifs(prev => [welcomeMsg, ...(prev || [])]);

        // 2. Show Persistent "Call to Action" Toast
        toast(
          t => (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: "250px" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "20px" }}>🐺</span>
                <b>RECRUITMENT NOTICE</b>
              </div>
              <span>You&apos;ve published content. Now it&apos;s time to hunt.</span>
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  handleNav("wolf_hunt"); // Navigate to Wolf Hunt tab
                }}
                style={{
                  background: "#00ff88",
                  color: "#000",
                  border: "none",
                  padding: "8px 12px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  marginTop: "8px",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                }}
              >
                REPORT FOR DUTY →
              </button>
              <small style={{ color: "#aaa", fontSize: "11px", marginTop: "4px" }}>
                ℹ️ Click the Speaker icon on the Hunt page for full briefing.
              </small>
            </div>
          ),
          {
            duration: 15000,
            position: "top-center",
            style: {
              background: "#111",
              border: "1px solid #00ff88",
              color: "#fff",
              boxShadow: "0 0 20px rgba(0,255,136,0.2)",
            },
          }
        );
      }
    }
  }, [contentList]); // Depend on contentList

  // Toggle dashboard-mode class on mount/unmount so global gradients don't show through dashboard pages
  useEffect(() => {
    // Fetch system health on dashboard load and log to console if any service degraded
    (async function checkSystemHealth() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/health`);
        const json = await res.json();
        if (!res.ok || (json && json.status && json.status !== "OK")) {
          setSystemHealth({
            ok: false,
            status: (json && json.status) || "degraded",
            message: json && json.message,
          });
          console.error("[SystemHealth] Dashboard detected degraded system:", json);
        } else {
          setSystemHealth({ ok: true, status: "OK", message: null });
        }
      } catch (e) {
        setSystemHealth({ ok: false, status: "error", message: e.message });
        console.error("[SystemHealth] Dashboard detected error checking health:", e && e.message);
      }
    })();

    document.documentElement?.classList?.add("dashboard-mode");
    document.body?.classList?.add("dashboard-mode");

    // Notification refreshes are UI work; hidden tabs must not keep reading Firestore.
    const pollNotifications = async signal => {
      try {
        const currentUser = auth?.currentUser;
        if (!currentUser) return;
        // Do NOT force refresh token (pass false), use cached token to prevent rate limiting
        const token = await currentUser.getIdToken(false);
        if (signal.aborted || document.hidden) return;
        const res = await fetch(`${API_ENDPOINTS.NOTIFICATIONS_LIST}?limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        });
        if (!res.ok) return;
        const json = await res.json();
        if (signal.aborted || auth?.currentUser?.uid !== currentUser.uid) return;
        if (!json.notifications) return;
        const incoming = Array.isArray(json.notifications) ? json.notifications : [];
        const incomingUnread = incoming.filter(notification => !notification?.read);
        const unseenRecentUnread = incomingUnread.filter(notification => {
          if (!notification?.id || notifiedNotificationIdsRef.current.has(notification.id)) {
            return false;
          }
          const createdAt = Date.parse(notification.created_at || notification.timestamp || "");
          return Number.isFinite(createdAt) && createdAt >= notificationSessionStartedAtRef.current;
        });

        incomingUnread.forEach(notification => {
          if (notification?.id) notifiedNotificationIdsRef.current.add(notification.id);
        });

        setNotifs(prevNotifs => {
          const currentList = Array.isArray(prevNotifs) ? prevNotifs : [];
          const syntheticNotifications = currentList.filter(notification =>
            String(notification?.id || "").startsWith("wolf-hunt-welcome-")
          );
          const merged = [...incomingUnread, ...syntheticNotifications].reduce(
            (acc, notification) => {
              const key =
                notification?.id ||
                `${notification?.title || "notification"}-${notification?.created_at || ""}`;
              if (
                !acc.some(
                  item =>
                    (item?.id || `${item?.title || "notification"}-${item?.created_at || ""}`) ===
                    key
                )
              ) {
                acc.push(notification);
              }
              return acc;
            },
            []
          );

          if (didHydrateNotificationPollRef.current && unseenRecentUnread.length > 0) {
            setTimeout(() => {
              unseenRecentUnread.forEach(notification => {
                try {
                  toast(
                    notification.message || notification.title || "You have a new notification"
                  );
                } catch (_) {}
              });
            }, 0);
          }

          didHydrateNotificationPollRef.current = true;
          return merged.slice(0, 200);
        });
      } catch (e) {
        // ignore polling errors
      }
    };
    const stopNotificationPolling = startVisiblePoller(pollNotifications);

    return () => {
      stopNotificationPolling();
      document.documentElement?.classList?.remove("dashboard-mode");
      document.body?.classList?.remove("dashboard-mode");
    };
  }, []); // Empty dependency array to run only once on mount

  const handleNav = useCallback(
    (tab, options = {}) => {
      const destinationTab = workspaceDestination(tab);
      if (destinationTab === "wolf_hunt" && !ENABLE_WOLF_HUNT) {
        toast("🐺 Wolf Hunt is currently locked. Come back later!", { icon: "🔒" });
        return;
      }
      if (destinationTab === "cam_combiner" && isMobileViewport) {
        toast("Cam Combiner is desktop-only because its editing canvas needs more screen space.", {
          icon: "🖥️",
        });
        setSidebarOpen(false);
        return;
      }
      if (destinationTab !== activeTab) {
        if (destinationTab === "billing") {
          setBillingReturnTab(activeTab);
        } else if (activeTab === "billing") {
          setBillingReturnTab(activeTab);
        }
      }
      setUploadLaunchTab(destinationTab === "upload" ? options?.uploadTab || null : null);
      setActiveTab(destinationTab);
      setSidebarOpen(false);
    },
    [ENABLE_WOLF_HUNT, activeTab, isMobileViewport]
  );
  const triggerSchedulesRefresh = useCallback(() => {
    onSchedulesChanged && onSchedulesChanged();
  }, [onSchedulesChanged]);

  const withAuth = useCallback(async cb => {
    const currentUser = auth?.currentUser;
    if (!currentUser) {
      toast.error("Please sign in first");
      return;
    }
    try {
      const token = await currentUser.getIdToken(true);
      return cb(token);
    } catch (error) {
      // Silently handle token refresh errors
      console.warn("Token refresh failed:", error.message);
      return null;
    }
  }, []);

  const doPause = useCallback(
    async id => {
      const result = await withAuth(async token => {
        const response = await fetch(API_ENDPOINTS.SCHEDULE_PAUSE(id), {
          method: "POST",
          headers: withWorkspaceHeaders({ Authorization: `Bearer ${token}` }),
        });
        if (!response.ok) throw new Error("Failed to pause destination");
        triggerSchedulesRefresh();
        toast.success("Destination paused");
        return true;
      });
      return result;
    },
    [withAuth, triggerSchedulesRefresh]
  );
  const doResume = useCallback(
    async id => {
      return withAuth(async token => {
        const response = await creditFetch(API_ENDPOINTS.SCHEDULE_RESUME(id), {
          method: "POST",
          headers: withWorkspaceHeaders({ Authorization: `Bearer ${token}` }),
        });
        if (!response.ok) throw new Error("Failed to resume destination");
        triggerSchedulesRefresh();
        toast.success("Destination resumed");
        return true;
      });
    },
    [withAuth, triggerSchedulesRefresh]
  );
  const doReschedule = useCallback(
    async (id, when, timezone) => {
      return withAuth(async token => {
        const response = await creditFetch(API_ENDPOINTS.SCHEDULE_RESCHEDULE(id), {
          method: "POST",
          headers: withWorkspaceHeaders({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }),
          body: JSON.stringify({ time: when, timezone }),
        });
        if (!response.ok) throw new Error("Failed to reschedule destination");
        triggerSchedulesRefresh();
        toast.success("Destination rescheduled");
        return true;
      });
    },
    [withAuth, triggerSchedulesRefresh]
  );
  const doReleaseAction = useCallback(
    async (ids, action, when, timezone) => {
      const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
      if (!uniqueIds.length) return false;
      return withAuth(async token => {
        const endpoint = id =>
          action === "pause"
            ? API_ENDPOINTS.SCHEDULE_PAUSE(id)
            : action === "resume"
              ? API_ENDPOINTS.SCHEDULE_RESUME(id)
              : API_ENDPOINTS.SCHEDULE_RESCHEDULE(id);
        const results = await Promise.allSettled(
          uniqueIds.map(async id => {
            const response = await creditFetch(endpoint(id), {
              method: "POST",
              headers: withWorkspaceHeaders({
                Authorization: `Bearer ${token}`,
                ...(action === "reschedule" ? { "Content-Type": "application/json" } : {}),
              }),
              ...(action === "reschedule"
                ? { body: JSON.stringify({ time: when, timezone }) }
                : {}),
            });
            if (!response.ok) throw new Error(`${id}: ${response.status}`);
          })
        );
        await triggerSchedulesRefresh();
        const failed = results.filter(result => result.status === "rejected").length;
        if (failed) {
          toast.error(
            `${failed}/${uniqueIds.length} destinations could not be ${action === "reschedule" ? "rescheduled" : action === "pause" ? "paused" : "resumed"}. Check each status before retrying.`
          );
          return false;
        }
        toast.success(
          `${uniqueIds.length} destinations ${action === "reschedule" ? "rescheduled" : action === "pause" ? "paused" : "resumed"}`
        );
        return true;
      });
    },
    [withAuth, triggerSchedulesRefresh]
  );
  const doEditScheduleCopy = useCallback(
    async (id, copy) => {
      try {
        const currentUser = auth?.currentUser;
        if (!currentUser) throw new Error("Sign in to edit scheduled copy");
        const token = await currentUser.getIdToken(true);
        const response = await fetch(API_ENDPOINTS.SCHEDULE_POST_COPY(id), {
          method: "PATCH",
          headers: withWorkspaceHeaders({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }),
          body: JSON.stringify(copy),
        });
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          throw new Error(detail.error || "Could not update scheduled copy");
        }
        await triggerSchedulesRefresh();
        toast.success("Platform post copy updated");
        return true;
      } catch (error) {
        toast.error(error.message || "Could not update scheduled copy");
        return false;
      }
    },
    [triggerSchedulesRefresh]
  );
  const doDelete = useCallback(
    async id => {
      if (
        !window.confirm(
          "Cancel this destination's scheduled post? The other destinations remain scheduled."
        )
      )
        return;
      await withAuth(async token => {
        try {
          const response = await fetch(API_ENDPOINTS.SCHEDULE_DELETE(id), {
            method: "DELETE",
            headers: withWorkspaceHeaders({ Authorization: `Bearer ${token}` }),
          });
          if (!response.ok && response.status !== 404) {
            throw new Error(`Delete failed with status ${response.status}`);
          }
          triggerSchedulesRefresh();
          toast.success("Destination cancelled");
        } catch (e) {
          console.warn(e);
          toast.error("Failed to cancel destination");
        }
      });
    },
    [withAuth, triggerSchedulesRefresh]
  );

  const doDeleteMany = useCallback(
    async ids => {
      const uniqueIds = Array.from(new Set((ids || []).filter(Boolean)));
      if (uniqueIds.length === 0) return;
      if (
        !window.confirm(
          `Cancel this release across ${uniqueIds.length} destination${uniqueIds.length === 1 ? "" : "s"}? This removes those scheduled posts.`
        )
      ) {
        return;
      }

      await withAuth(async token => {
        const toastId = toast.loading(
          `Cancelling ${uniqueIds.length} destination${uniqueIds.length === 1 ? "" : "s"}...`
        );
        try {
          const results = await Promise.allSettled(
            uniqueIds.map(async id => {
              const response = await fetch(API_ENDPOINTS.SCHEDULE_DELETE(id), {
                method: "DELETE",
                headers: withWorkspaceHeaders({ Authorization: `Bearer ${token}` }),
              });
              return { id, ok: response.ok || response.status === 404, status: response.status };
            })
          );
          const failed = results.filter(result => result.status === "rejected" || !result.value.ok);
          await triggerSchedulesRefresh();
          if (failed.length > 0) {
            toast.error(
              `${failed.length} destination${failed.length === 1 ? "" : "s"} could not be cancelled`,
              {
                id: toastId,
              }
            );
          } else {
            toast.success(
              `Cancelled ${uniqueIds.length} destination${uniqueIds.length === 1 ? "" : "s"}`,
              {
                id: toastId,
              }
            );
          }
        } catch (e) {
          console.warn(e);
          toast.error("Failed to cancel selected destinations", { id: toastId });
        }
      });
    },
    [withAuth, triggerSchedulesRefresh]
  );

  const refreshAllStatus = loadAllPlatformStatusesUnified;
  const telegramConnection = useTelegramConnection({ uid: user?.uid,
    connected: telegramStatus?.connected === true, refreshStatus: loadPlatformStatus });
  const snapchatConnectPending = useRef(0);

  useEffect(() => {
    const refreshSnapchat = () => {
      clearCache();
      // Read Snapchat's uncached status last: a different API instance may still
      // have a briefly cached aggregate from before the callback completed.
      refreshAllStatus().then(() => loadPlatformStatus("snapchat"));
    };
    const onSnapchatComplete = event => {
      if (
        event.origin !== new URL(API_BASE_URL, window.location.origin).origin ||
        event.data !== "snapchat_oauth_complete"
      )
        return;
      // The message only triggers an authenticated server refresh; it cannot
      // mark an account connected or grant publishing rights on its own.
      snapchatConnectPending.current = 0;
      refreshSnapchat();
    };
    // Browser opener isolation can sever OAuth popup messages. Returning to the
    // original tab then refreshes its authenticated status, never client claims.
    const onReturn = () => {
      if (
        document.hidden ||
        !snapchatConnectPending.current ||
        Date.now() - snapchatConnectPending.current > 15 * 60 * 1000
      )
        return;
      refreshSnapchat();
    };
    window.addEventListener("message", onSnapchatComplete);
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("message", onSnapchatComplete);
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [refreshAllStatus, loadPlatformStatus]);

  useEffect(() => {
    // Check URL params for OAuth callback success/error
    const params = new URLSearchParams(window.location.search);
    if (window.location.hash && window.location.hash.includes("?")) {
      const hashQs = window.location.hash.split("?")[1];
      const hashParams = new URLSearchParams(hashQs);
      for (const [key, value] of hashParams.entries()) params.set(key, value);
    }

    const hasPayPalReturn =
      params.has("payment") ||
      params.has("subscription_id") ||
      params.has("subscriptionId") ||
      params.has("ba_token");

    if (hasPayPalReturn) {
      setActiveTab("billing");
      hasAutoRoutedPrimaryTab.current = true;
    }

    const oauthPlatform =
      params.get("oauth") ||
      params.get("youtube") ||
      params.get("tiktok") ||
      params.get("facebook") ||
      params.get("twitter") ||
      params.get("spotify") ||
      params.get("discord") ||
      params.get("reddit") ||
      params.get("linkedin") ||
      params.get("pinterest") ||
      params.get("telegram") ||
      params.get("snapchat");
    const oauthStatus = params.get("status");

    if (oauthPlatform) {
      // Clear URL params without reload
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);

      // Clear ALL platform status caches to force fresh data
      clearCache();

      // Show toast notification
      if (oauthStatus === "success" || params.get(oauthPlatform) === "connected") {
        setConnectBanner({
          type: "success",
          message: `${oauthPlatform.charAt(0).toUpperCase() + oauthPlatform.slice(1)} connected successfully!`,
        });
        toast.success(
          `${oauthPlatform.charAt(0).toUpperCase() + oauthPlatform.slice(1)} connected successfully!`
        );
        // Auto-dismiss after 5 seconds
        setTimeout(() => setConnectBanner(null), 5000);
      } else if (oauthStatus === "error" || params.get(oauthPlatform) === "error") {
        setConnectBanner({
          type: "error",
          message: `Failed to connect ${oauthPlatform}. Please try again.`,
        });
        toast.error(`Failed to connect ${oauthPlatform}. Please try again.`);
        setTimeout(() => setConnectBanner(null), 5000);
      }

      // Trigger refresh of all platform statuses with cleared cache
      setTimeout(() => refreshAllStatus(), 500);
    }

    // Load initial data with caching and rate limit protection
    const loadInitial = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        // Use cached token unless expired to improve reliability
        const token = await currentUser.getIdToken();

        // Load critical data first (with caching)
        await cachedFetch(
          "initial-data",
          async () => {
            return true;
          },
          60000
        ); // 60s cache

        // Load all platform statuses from the unified endpoint
        await loadAllPlatformStatusesUnified();
      } catch (e) {
        /* ignore */
      }
    };
    // If user is already present, run initial load. Otherwise wait for auth state.
    const currentUser = auth.currentUser;
    if (currentUser) {
      loadInitial();
    } else {
      // Wait for auth state to initialize then run loadInitial once
      const unsubscribe = auth.onAuthStateChanged(u => {
        if (u) {
          // run initial load once
          loadInitial().catch(() => {});
          unsubscribe();
        }
      });
    }
  }, []);

  useEffect(() => {
    const rawPlatforms =
      platformSummary && platformSummary.raw ? Object.values(platformSummary.raw) : [];
    if (hasAutoRoutedPrimaryTab.current || rawPlatforms.length === 0) return;
    if (activeTab !== "profile") {
      hasAutoRoutedPrimaryTab.current = true;
      return;
    }

    setActiveTab(
      rawPlatforms.some(platform => platform && platform.connected) ? "upload" : "connections"
    );
    hasAutoRoutedPrimaryTab.current = true;
  }, [platformSummary, activeTab]);

  const setPlatformOption = useCallback((platform, key, value) => {
    setPlatformOptions(prev => ({
      ...(prev || {}),
      [platform]: { ...((prev || {})[platform] || {}), [key]: value },
    }));
  }, []);

  const togglePlatform = useCallback(name => {
    setSelectedPlatforms(prev =>
      prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name]
    );
  }, []);

  // Small helper for default platform toggles
  const toggleDefaultPlatform = useCallback(name => {
    setDefaultsPlatforms(prev =>
      prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name]
    );
  }, []);

  const handleSaveDefaults = async () => {
    if (!onSaveDefaults) return;
    try {
      const saved = await onSaveDefaults({
        timezone: tz,
        defaultPlatforms: defaultsPlatforms,
        defaultFrequency: defaultsFrequency,
        paypalEmail,
      });
      if (!saved) throw new Error("save_failed");
      toast.success("Defaults saved successfully!");
    } catch (e) {
      toast.error("Failed to save defaults");
    }
  };

  // Connect handlers; these call the generic openProviderAuth where appropriate
  const handleConnectTikTok = async () => openProviderAuth(API_ENDPOINTS.TIKTOK_AUTH_START);
  const handleConnectFacebook = async () => openProviderAuth(API_ENDPOINTS.FACEBOOK_AUTH_START);
  const handleConnectYouTube = async () => openProviderAuth(API_ENDPOINTS.YOUTUBE_AUTH_START);
  const handleConnectTwitter = async () => {
    // Prompt the user to choose OAuth1 (recommended for native video uploads) or OAuth2 PKCE.
    const useOauth1 = window.confirm(
      "Connect with Twitter: click OK to use OAuth1 (recommended for video uploads), or Cancel to use standard OAuth2 PKCE."
    );
    const endpoint = useOauth1
      ? API_ENDPOINTS.TWITTER_AUTH_PREPARE_OAUTH1 ||
        API_ENDPOINTS.TWITTER_AUTH_PREPARE ||
        API_ENDPOINTS.TWITTER_AUTH_START
      : API_ENDPOINTS.TWITTER_AUTH_PREPARE || API_ENDPOINTS.TWITTER_AUTH_START;
    if (useOauth1) toast("Opening OAuth1 authentication (recommended for video uploads)");
    else toast("Opening standard Twitter authentication");
    await openProviderAuth(endpoint);
  };
  const handleConnectSnapchat = async () => {
    snapchatConnectPending.current = Date.now();
    await openProviderAuth(
      API_ENDPOINTS.SNAPCHAT_AUTH_PREPARE || API_ENDPOINTS.SNAPCHAT_AUTH_START
    );
  };
  const handleConnectSpotify = async () => openProviderAuth(API_ENDPOINTS.SPOTIFY_AUTH_START);
  const handleConnectReddit = async () => openProviderAuth(API_ENDPOINTS.REDDIT_AUTH_START);
  const handleConnectDiscord = async () => openProviderAuth(API_ENDPOINTS.DISCORD_AUTH_START);
  const handleConnectLinkedin = async () => openProviderAuth(API_ENDPOINTS.LINKEDIN_AUTH_START);
  const handleConnectTelegram = async () => {
    if (getActiveWorkspaceId() && getActiveWorkspaceRole() !== "owner") {
      toast.error("Only the workspace owner can connect publishing destinations.");
      return;
    }
    await telegramConnection.prepare();
  };
  const handleConnectPinterest = async () => openProviderAuth(API_ENDPOINTS.PINTEREST_AUTH_START);

  const handleDisconnectPlatform = async platform => {
    if (!window.confirm(`Disconnect ${platform}?`)) return;
    await withAuth(async token => {
      try {
        const res = await fetch(API_ENDPOINTS.PLATFORM_DISCONNECT(platform), {
          method: "POST",
          headers: withWorkspaceHeaders({ Authorization: `Bearer ${token}` }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || "Failed to disconnect");
        }

        // Show success banner
        setConnectBanner({
          type: "success",
          message: `${platform.charAt(0).toUpperCase() + platform.slice(1)} disconnected successfully`,
        });
        setTimeout(() => setConnectBanner(null), 4000);

        // Immediately update local state, then refresh from server
        setPlatformStatusByName(platform, { connected: false, meta: null });
        await refreshAllStatus();
      } catch (e) {
        console.warn(e);
        setConnectBanner({ type: "error", message: e.message || "Failed to disconnect" });
        setTimeout(() => setConnectBanner(null), 4000);
        toast.error(e.message || "Failed to disconnect");
      }
    });
  };

  const markAllNotificationsRead = async () => {
    try {
      await withAuth(async token => {
        await fetch(API_ENDPOINTS.NOTIFICATIONS_MARK_READ, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
      });
      setNotifs([]);
      toast.success("All notifications marked as read");
    } catch (e) {
      console.warn(e);
      toast.error("Failed to mark notifications as read");
    }
  };

  const openProviderAuth = async endpointUrl => {
    if (getActiveWorkspaceId() && getActiveWorkspaceRole() !== "owner") {
      toast.error("Only the workspace owner can connect publishing destinations.");
      return;
    }
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error("Please sign in first");
        return;
      }
      const token = await currentUser.getIdToken(true);
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

      // If the endpoint is a "prepare" endpoint, POST to it to retrieve the authUrl
      const isPrepareEndpoint =
        String(endpointUrl).includes("/prepare") || String(endpointUrl).endsWith("/oauth/prepare");
      if (isPrepareEndpoint) {
        try {
          const prepareRes = await fetch(endpointUrl, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ popup: true }),
          });
          const prepareData = await prepareRes.json().catch(() => null);
          if (!prepareRes.ok) {
            const msg =
              prepareData && (prepareData.error || prepareData.details || prepareData.message)
                ? prepareData.error || prepareData.details || prepareData.message
                : "Auth prepare failed";
            console.warn(
              "Prepare endpoint POST returned error",
              prepareRes.status,
              msg,
              prepareData
            );
            toast.error(msg);
            return;
          }
          if (!prepareData?.authUrl) {
            toast.error("Auth prepare failed: no authUrl returned");
            return;
          }
          // If provider probe returned 5xx or probe error, surface helpful error and do not open provider page
          const probeStatus = prepareData.probeStatus;
          if (
            probeStatus === "probe_error" ||
            (typeof probeStatus === "number" && probeStatus >= 500)
          ) {
            console.warn(
              "Provider probe indicates an error, aborting open. probeStatus=",
              probeStatus,
              prepareData
            );
            toast.error(
              "Provider temporarily unavailable. Please try again later or contact support."
            );
            return;
          }
          if (isMobile && prepareData.appUrl) {
            if (!isAllowedAuthUrl(prepareData.appUrl)) {
              toast.error("Untrusted redirect URL blocked.");
              return;
            }
            window.location.href = prepareData.appUrl;
          } else if (isMobile) {
            if (!isAllowedAuthUrl(prepareData.authUrl)) {
              toast.error("Untrusted redirect URL blocked.");
              return;
            }
            window.location.href = prepareData.authUrl;
          } else {
            if (!isAllowedAuthUrl(prepareData.authUrl)) {
              toast.error("Untrusted redirect URL blocked.");
              return;
            }
            window.open(prepareData.authUrl, "_blank");
          }
          toast.success("Opening authentication window...");
          return;
        } catch (err) {
          console.warn("Prepare endpoint POST failed:", err.message);
          toast.error("Failed to start authentication. Please try again.");
          return; // don't try to GET a prepare endpoint
        }
      }

      // First, check if this is a two-step flow (returns JSON with prepareUrl) or direct redirect
      // Only attempt the `fetch` probe when the endpoint is same-origin to avoid CORS issues
      try {
        const sameOrigin = (() => {
          try {
            const u = new URL(endpointUrl, window.location.href);
            return u.origin === window.location.origin;
          } catch (e) {
            return false;
          }
        })();

        if (sameOrigin) {
          // Try GET first to see if the provider uses a two-step flow (returns JSON)
          const checkRes = await fetch(endpointUrl, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          });
          const contentType = checkRes.headers.get("content-type");
          if (contentType?.includes("application/json")) {
            // Two-step flow: GET returns JSON with prepareUrl, then POST to prepare
            const data = await checkRes.json();
            if (data.prepareUrl) {
              // POST to prepare endpoint to get the actual auth URL
              const prepareRes = await fetch(data.prepareUrl, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
              });
              const prepareData = await prepareRes.json();
              if (!prepareRes.ok) {
                const msg =
                  prepareData && (prepareData.error || prepareData.details || prepareData.message)
                    ? prepareData.error || prepareData.details || prepareData.message
                    : "Auth prepare failed";
                toast.error(msg);
                return;
              }
              if (!prepareData?.authUrl) {
                toast.error("Auth prepare failed: no authUrl returned");
                return;
              }
              if (isMobile) {
                if (!isAllowedAuthUrl(prepareData.authUrl)) {
                  toast.error("Untrusted redirect URL blocked.");
                  return;
                }
                window.location.href = prepareData.authUrl;
              } else {
                if (!isAllowedAuthUrl(prepareData.authUrl)) {
                  toast.error("Untrusted redirect URL blocked.");
                  return;
                }
                window.open(prepareData.authUrl, "_blank");
              }
              toast.success("Opening authentication window...");
              return;
            }
          }
        } else {
          // Skip probing cross-origin auth endpoints to avoid CORS redirect errors.
          console.debug("Skipping cross-origin auth probe for", endpointUrl);
        }
      } catch (jsonErr) {
        // Not JSON or fetch failed; fall through to direct redirect approach
        console.warn("Two-step auth not available, using direct redirect", jsonErr.message);
      }

      // Direct redirect flow: append token as query param and open
      const separator = endpointUrl.includes("?") ? "&" : "?";
      const authUrl = `${endpointUrl}${separator}id_token=${encodeURIComponent(token)}`;
      if (isMobile) {
        if (!isAllowedAuthUrl(authUrl)) {
          toast.error("Untrusted redirect URL blocked.");
          return;
        }
        window.location.href = authUrl;
      } else {
        if (!isAllowedAuthUrl(authUrl)) {
          toast.error("Untrusted redirect URL blocked.");
          return;
        }
        window.open(authUrl, "_blank");
      }
      toast.success("Opening authentication window...");
    } catch (e) {
      console.warn(e);
      toast.error(e.message || "Failed to start auth");
    }
  };

  const currentPageMeta = DASHBOARD_PAGE_META[activeTab] || {
    eyebrow: "AutoPromote",
    title: "Dashboard",
    description: "Manage your content workflow.",
  };

  return (
    <div
      className={`dashboard-root ap-dashboard-redesign ${activeTab === "live" ? "live-mode" : ""}`}
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#101a2e",
            color: "#f8fafc",
            border: "1px solid rgba(148, 163, 184, 0.18)",
          },
        }}
      />
      {/* TopNav removed for live tab as requested */}
      {activeTab !== "live" && (
        <header className="dashboard-topbar" aria-label="Top navigation">
          <button
            className="hamburger"
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen(v => !v)}
          >
            <span />
            <span />
            <span />
          </button>
          <div className="topbar-page-copy">
            <span>{currentPageMeta.eyebrow}</span>
            <strong>{currentPageMeta.title}</strong>
          </div>
          <div className="topbar-actions">
            <ProductAssistanceLauncher className="topbar-icon-btn" />
            <VoiceOverGuide activeTab={activeTab} />
            <button
              className={`topbar-icon-btn ${activeTab === "notifications" ? "is-active" : ""}`}
              aria-label="Notifications"
              onClick={() => handleNav("notifications")}
            >
              <DashboardIcon name="notifications" size={20} />
              {notifs.length > 0 && (
                <span className="topbar-notification-badge">
                  {notifs.length > 9 ? "9+" : notifs.length}
                </span>
              )}
            </button>
            <div className="topbar-user-card" title={user?.email || user?.name || "Account"}>
              <img src={user?.avatarUrl || DEFAULT_IMAGE} alt="" />
              <span>
                <strong>{user?.name || "AutoPromote user"}</strong>
                <small>{user?.email || "Creator workspace"}</small>
              </span>
            </div>
          </div>
        </header>
      )}

      {activeTab !== "live" && sidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close dashboard menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {activeTab !== "live" && (
        <aside className={`dashboard-sidebar ${sidebarOpen ? "open" : ""}`} aria-label="Sidebar">
          <div className="dashboard-brand">
            <AutoPromoteLogo />
          </div>
          <nav className="dashboard-navbar-vertical" role="navigation">
            {DASHBOARD_NAV_GROUPS.map(group => {
              const visibleItems = group.items.filter(
                item => !item.desktopOnly || !isMobileViewport
              );
              if (visibleItems.length === 0) return null;
              return (
                <section className="dashboard-nav-group" key={group.label}>
                  {group.label ? <p>{group.label}</p> : null}
                  <ul>
                    {visibleItems.map(item => (
                      <DashboardNavItem
                        key={item.id}
                        item={item}
                        activeTab={activeTab}
                        onNavigate={handleNav}
                      />
                    ))}
                  </ul>
                </section>
              );
            })}

            {isAdminUser && (
              <section className="dashboard-nav-group">
                <p>Administration</p>
                <ul>
                  {onOpenAdminDashboard && (
                    <DashboardNavItem
                      item={{ id: "admin-dashboard", label: "Admin dashboard", icon: "audit" }}
                      activeTab={activeTab}
                      onNavigate={onOpenAdminDashboard}
                    />
                  )}
                  <DashboardNavItem
                    item={{ id: "admin-audit", label: "Admin Audit", icon: "audit" }}
                    activeTab={activeTab}
                    onNavigate={handleNav}
                  />
                  <DashboardNavItem
                    item={{ id: "admin-kyc", label: "Admin KYC", icon: "identity" }}
                    activeTab={activeTab}
                    onNavigate={handleNav}
                  />
                </ul>
              </section>
            )}

            {ENABLE_WOLF_HUNT && (
              <section className="dashboard-nav-group">
                <p>Labs</p>
                <ul>
                  {ENABLE_WOLF_HUNT && (
                    <DashboardNavItem
                      item={{ id: "wolf_hunt", label: "Mission Board", icon: "target" }}
                      activeTab={activeTab}
                      onNavigate={handleNav}
                    />
                  )}
                </ul>
              </section>
            )}
          </nav>
          <div className="dashboard-sidebar-footer">
            <a className="dashboard-help-card" href="/docs">
              <span>Need help getting started?</span>
              <strong>Visit the docs →</strong>
            </a>
            <button className="logout-btn" onClick={onLogout}>
              <DashboardIcon name="logout" />
              <span>Sign out</span>
            </button>
          </div>
        </aside>
      )}

      <main className="dashboard-main">
        {activeTab !== "live" && (
          <header className="dashboard-page-header">
            <div>
              <span>{currentPageMeta.eyebrow}</span>
              <h1>{currentPageMeta.title}</h1>
              <p>{currentPageMeta.description}</p>
            </div>
          </header>
        )}
        {testerAccessActive && (
          <section className="founding-tester-banner" aria-label="Founding Tester access">
            <div>
              <span>✨ Founding Tester</span>
              <strong>Your controlled 30-day test pass is active</strong>
              <p>
                Studio, Cam Combiner, Publisher, Queue and Find Viral Clips are ready.
                Other tools stay locked. Your 1,500-credit trial allowance expires with the pass; it
                does not renew or charge you.
              </p>
              <small>
                {Number(user?.totalCredits || 0)} credits remaining · 10 uploads · 30 queued posts ·
                3 connected platforms
              </small>
            </div>
            <div className="founding-tester-banner__actions">
              <small>Expires {new Date(testerAccess.expiresAt).toLocaleDateString()}</small>
              <button
                type="button"
                onClick={() => handleNav(isMobileViewport ? "upload" : "cam_combiner")}
              >
                {isMobileViewport ? "Start publishing" : "Start with Cam Combiner"}
              </button>
            </div>
          </section>
        )}
        {!emailVerified && (
          <div
            className="verification-banner"
            style={{
              background: "#fff3cd",
              color: "#856404",
              padding: "12px",
              marginBottom: "16px",
              borderRadius: "8px",
              border: "1px solid #ffeeba",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "0.95rem",
            }}
          >
            <span>
              {!hasConnectedPlatforms && (
                <div
                  style={{
                    marginBottom: "1rem",
                    padding: "14px 16px",
                    borderRadius: 14,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    color: "#1e3a8a",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <strong>Start here:</strong> connect at least one platform before your first
                    publish workflow.
                  </div>
                  <button className="check-quality" onClick={() => handleNav("connections")}>
                    Connect Platforms
                  </button>
                </div>
              )}
              {hasConnectedPlatforms && contentList.length === 0 && (
                <div
                  style={{
                    marginBottom: "1rem",
                    padding: "14px 16px",
                    borderRadius: 14,
                    background: "#ecfeff",
                    border: "1px solid #a5f3fc",
                    color: "#155e75",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <strong>Workspace ready:</strong> upload once, choose destinations, and inspect
                    status from one queue.
                  </div>
                  <button className="check-quality" onClick={() => handleNav("upload")}>
                    Publish Your First Asset
                  </button>
                </div>
              )}
              <strong>Verify your email:</strong> Please check your inbox for a verification link.
              Verify to access security features.
            </span>
            <button
              onClick={async () => {
                if (auth.currentUser) {
                  try {
                    await sendEmailVerification(auth.currentUser);
                    toast.success("Verification email sent!");
                  } catch (e) {
                    toast.error("Error sending email: " + e.message);
                  }
                }
              }}
              style={{
                background: "transparent",
                border: "1px solid #856404",
                color: "#856404",
                padding: "4px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "bold",
                marginLeft: "12px",
              }}
            >
              Resend Email
            </button>
          </div>
        )}
        {systemHealth && !systemHealth.ok && (
          <div
            style={{
              padding: "8px 12px",
              background: "#ffebee",
              color: "#b71c1c",
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            ⚠️ System status degraded: {systemHealth.status}{" "}
            {systemHealth.message ? ` - ${systemHealth.message}` : ""}
          </div>
        )}
        {connectBanner && (
          <div
            className={`connect-banner ${connectBanner.type}`}
            style={{
              padding: "1rem",
              marginBottom: "1rem",
              borderRadius: "8px",
              background: connectBanner.type === "success" ? "#10b981" : "#ef4444",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>{connectBanner.message}</span>
            <button
              onClick={() => setConnectBanner(null)}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: "1.2rem",
                padding: "0 0.5rem",
              }}
            >
              ×
            </button>
          </div>
        )}
        {activeTab === "profile" && (
          <ProfilePanel
            user={user}
            stats={stats}
            tiktokStatus={tiktokStatus}
            facebookStatus={facebookStatus}
            youtubeStatus={youtubeStatus}
            twitterStatus={twitterStatus}
            snapchatStatus={snapchatStatus}
            spotifyStatus={spotifyStatus}
            redditStatus={redditStatus}
            discordStatus={discordStatus}
            linkedinStatus={linkedinStatus}
            telegramStatus={telegramStatus}
            pinterestStatus={pinterestStatus}
            tz={tz}
            defaultsPlatforms={defaultsPlatforms}
            defaultsFrequency={defaultsFrequency}
            paypalEmail={paypalEmail}
            setPaypalEmail={setPaypalEmail}
            toggleDefaultPlatform={toggleDefaultPlatform}
            setDefaultsFrequency={setDefaultsFrequency}
            setTz={setTz}
            handleSaveDefaults={handleSaveDefaults}
            handleConnectTikTok={handleConnectTikTok}
            handleConnectFacebook={handleConnectFacebook}
            handleConnectYouTube={handleConnectYouTube}
            handleConnectTwitter={handleConnectTwitter}
            handleConnectSnapchat={handleConnectSnapchat}
            handleConnectSpotify={handleConnectSpotify}
            handleConnectReddit={handleConnectReddit}
            handleConnectDiscord={handleConnectDiscord}
            handleConnectLinkedin={handleConnectLinkedin}
            handleConnectTelegram={handleConnectTelegram}
            handleConnectPinterest={handleConnectPinterest}
            onNavigate={handleNav}
          />
        )}

        {activeTab === "upload" && (
          <UploadPanel
            onUpload={onUpload}
            initialFile={selectedFile}
            onClearInitialFile={() => setSelectedFile(null)}
            initialTabOverride={uploadLaunchTab}
            onInitialTabHandled={() => setUploadLaunchTab(null)}
            contentList={contentList}
            defaultTimezone={userDefaults?.timezone}
            platformMetadata={platformMetadata}
            platformOptions={platformOptions}
            setPlatformOption={setPlatformOption}
            selectedPlatforms={selectedPlatforms}
            setSelectedPlatforms={setSelectedPlatforms}
            spotifySelectedTracks={spotifySelectedTracks}
            setSpotifySelectedTracks={setSpotifySelectedTracks}
            onNavigate={handleNav}
          />
        )}

        {activeTab === "clip_studio" && (
          <ClipStudioWorkspace
            initialFile={selectedFile}
            onUpgrade={() => handleNav("billing")}
            onOpenPublisher={(file, clip) => {
              if (file && typeof file === "object") {
                try {
                  Object.assign(file, {
                    suggestedTitle: clip?.hookText || clip?.reason || file.suggestedTitle,
                    workflowAction: "viral-clip-rendered",
                  });
                } catch (_) {
                  // The rendered file remains valid if this browser prevents custom File properties.
                }
              }
              setSelectedFile(file);
              handleNav("upload");
              toast.success("Clip Studio render is ready to publish.");
            }}
          />
        )}

        {activeTab === "viral_clip_studio" && (
          <ViralClipStudioPanel
            initialFile={selectedFile}
            onUpgrade={() => handleNav("billing")}
            onOpenPublisher={file => {
              if (file && typeof file === "object") {
                try {
                  Object.assign(file, { source: file.source || "viral_clip_studio" });
                } catch (_) {
                  // The rendered media remains usable if custom File properties are unavailable.
                }
              }
              setSelectedFile(file);
              handleNav("upload");
              toast.success("Viral Clip Studio render is ready to publish.");
            }}
          />
        )}

        {activeTab === "schedules" && (
          <SchedulesPanel
            schedulesList={schedulesList}
            loading={mySchedulesLoading}
            loadError={mySchedulesError}
            defaultTimezone={userDefaults?.timezone}
            contentList={contentList}
            scheduleScope={scheduleScope}
            hasMore={hasMoreSchedules}
            onScopeChanged={onScheduleScopeChanged}
            onLoadMore={onLoadMoreSchedules}
            onPause={doPause}
            onResume={doResume}
            onReschedule={doReschedule}
            onEditCopy={doEditScheduleCopy}
            onRescheduleMany={(ids, when, timezone) =>
              doReleaseAction(ids, "reschedule", when, timezone)
            }
            onPauseMany={ids => doReleaseAction(ids, "pause")}
            onResumeMany={ids => doReleaseAction(ids, "resume")}
            onDelete={doDelete}
            onDeleteMany={doDeleteMany}
            onNavigate={handleNav}
          />
        )}

        {activeTab === "rewards" && <RewardsPanel badges={badges} />}

        {activeTab === "notifications" && (
          <NotificationsPanel
            notifs={notifs}
            onMarkAllRead={markAllNotificationsRead}
            onNavigate={handleNav}
          />
        )}

        {activeTab === "ads" && <MissionControlPanel />}

        {activeTab === "billing" && (
          <div style={{ padding: "0 0 16px" }}>
            <button
              type="button"
              onClick={() => handleNav(billingReturnTab || "profile")}
              aria-label={`Go back to ${BILLING_RETURN_TAB_LABELS[billingReturnTab] || "previous section"}`}
              style={{
                marginBottom: "12px",
                border: "1px solid #4f46e5",
                background: "#4f46e5",
                color: "#ffffff",
                padding: "10px 18px",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: 700,
                lineHeight: 1,
                fontSize: "0.95rem",
                boxShadow: "0 2px 10px rgba(79, 70, 229, 0.35)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = "#4338ca";
                e.currentTarget.style.borderColor = "#4338ca";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = "#4f46e5";
                e.currentTarget.style.borderColor = "#4f46e5";
              }}
              onMouseDown={e => {
                e.currentTarget.style.transform = "translateY(1px)";
              }}
              onMouseUp={e => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Back to {BILLING_RETURN_TAB_LABELS[billingReturnTab] || "previous section"}
            </button>
            <PayPalSubscriptionPanel />
          </div>
        )}

        <TelegramConnectDialog state={telegramConnection.dialog} onClose={telegramConnection.close}
          onRetry={handleConnectTelegram} />
        {activeTab === "connections" && (
          <ConnectionsPanel
            platformSummary={platformSummary}
            discordStatus={discordStatus}
            spotifyStatus={spotifyStatus}
            redditStatus={redditStatus}
            youtubeStatus={youtubeStatus}
            twitterStatus={twitterStatus}
            tiktokStatus={tiktokStatus}
            facebookStatus={facebookStatus}
            linkedinStatus={linkedinStatus}
            snapchatStatus={snapchatStatus}
            telegramStatus={telegramStatus}
            pinterestStatus={pinterestStatus}
            handleConnectSpotify={handleConnectSpotify}
            handleConnectDiscord={handleConnectDiscord}
            handleConnectReddit={handleConnectReddit}
            handleConnectYouTube={handleConnectYouTube}
            handleConnectTwitter={handleConnectTwitter}
            handleConnectSnapchat={handleConnectSnapchat}
            handleConnectLinkedin={handleConnectLinkedin}
            handleConnectTelegram={handleConnectTelegram}
            handleConnectPinterest={handleConnectPinterest}
            handleConnectTikTok={handleConnectTikTok}
            handleConnectFacebook={handleConnectFacebook}
            handleDisconnectPlatform={handleDisconnectPlatform}
          />
        )}

        {activeTab === "team" && (
          <TeamPanel onWorkspaceChanged={onWorkspaceChanged} onNavigate={handleNav} />
        )}

        {activeTab === "admin-audit" && isAdminUser && <AdminAuditViewer />}

        {activeTab === "admin-kyc" && isAdminUser && <AdminKyc />}

        {/* KYC upload flow removed for live-only AfterDark */}

        {activeTab === "security" && <SecurityPanel user={user} />}

        {activeTab === "wolf_hunt" && <WolfHuntDashboard />}

        {activeTab === "cam_combiner" && !isMobileViewport && (
          <CamCombinerPanel
            onClose={() => handleNav("profile")}
            onFindViralClips={source => {
              if (!source?.renderJobId || !source?.url) return;
              setSelectedFile(source);
              handleNav("clip_studio");
              toast.success("Opening Clip Studio with the saved master — no re-upload needed.");
            }}
            onUseExport={result => {
              const publishAsset =
                result?.file instanceof File || result?.file instanceof Blob
                  ? result.file
                  : typeof result?.file === "string" && result.file.startsWith("http")
                    ? result.file
                    : typeof result?.url === "string" && result.url.startsWith("http")
                      ? result.url
                      : "";
              if (!publishAsset) {
                toast.error("This Cam Combiner master has no usable file or cloud URL.");
                return;
              }
              setSelectedFile(publishAsset);
              handleNav("upload");
              toast.success("Cam Combiner export is ready to publish.");
            }}
          />
        )}
      </main>
      <BottomNav activeTab={activeTab} onNav={handleNav} />
    </div>
  );
};

export default UserDashboard;
